// src/components/filter/UnitFilterContainer.js

import React, { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { IconChevronDown } from '@tabler/icons-react';
import UnitFilter from './UnitFilter';
import SingleUnitFilter from './SingleUnitFilter';
import { selectUnits, selectUnitId, selectUnitsLedByUser, selectSelectedUnitIds, setSelectedUnitIds, setUnitId } from '../../features/unitsSlice';
import { fetchCheckpoints } from '../../features/checkpointsSlice';
import './UnitFilter.css';
import './SingleUnitFilter.css';

const UnitFilterContainer = ({ 
    restrictToLedUnits, 
    singleSelect = false,
    flatMode = false,
    hideDisabledOptions = false,
    disabledTooltip = null,
    leaderUnitIds = []
}) => {
    const dispatch = useDispatch();
    const location = useLocation();
    
    // Track the previous unitIds to only fetch when they actually change
    const prevUnitIdsRef = useRef(null);

    // Determine if we're on the Checkpoints page
    const isCheckpointsPage = location.pathname === '/checkpoints';
    
    // Restrict to units led by user if explicitly requested or if on Checkpoints page
    const shouldRestrictToLedUnits = restrictToLedUnits || isCheckpointsPage;

    // Selectors to get units and selectedUnitId from the store
    const allUnits = useSelector(selectUnits);
    const unitsLedByUser = useSelector(selectUnitsLedByUser);
    const selectedUnitId = useSelector(selectUnitId);
    const selectedUnitIds = useSelector(selectSelectedUnitIds);
    const unitsStatus = useSelector((state) => state.units.status);
    const unitsError = useSelector((state) => state.units.error);
    const checkpointsLastFetchedUnitIds = useSelector((state) => state.checkpoints.lastFetchedUnitIds || []);

    // Use either all units or only units led by user based on context
    const units = shouldRestrictToLedUnits ? unitsLedByUser : allUnits;

    // Units are fetched in Layout.js - no need to fetch here

    // Fetch checkpoints when the selectedUnitIds change (and are different from what we already have)
    useEffect(() => {
        if (selectedUnitIds && selectedUnitIds.length > 0) {
            // Check if the selected units are different from previously fetched
            const prevIds = prevUnitIdsRef.current || [];
            const lastFetchedIds = checkpointsLastFetchedUnitIds || [];
            
            const idsChanged = selectedUnitIds.length !== prevIds.length || 
                               selectedUnitIds.some((id, idx) => id !== prevIds[idx]);
            
            const needsFetch = selectedUnitIds.length !== lastFetchedIds.length || 
                               selectedUnitIds.some((id) => !lastFetchedIds.includes(id));
            
            if (idsChanged && needsFetch) {
                dispatch(fetchCheckpoints());
                prevUnitIdsRef.current = [...selectedUnitIds];
            }
        }
    }, [selectedUnitIds, checkpointsLastFetchedUnitIds, dispatch]);

    // Handler for unit selection (multi)
    const handleSelectUnits = (unitIds) => {
        dispatch(setSelectedUnitIds(unitIds));
        // Fetch checkpoints will be triggered by the useEffect watching selectedUnitIds
    };

    // Handler for unit selection (single)
    const handleSelectUnit = (unitId) => {
        dispatch(setUnitId(unitId));
        dispatch(setSelectedUnitIds(unitId ? [unitId] : []));
        // Fetch checkpoints will be triggered by the useEffect watching selectedUnitIds
    };

    // If no selection exists yet but units are loaded, select the default one (first visible)
    // Only auto-select for single select mode, not for multi-select
    useEffect(() => {
        if (unitsStatus === 'succeeded' && units.length > 0 && singleSelect) {
            const firstVisible = units.find((u) => u.visibility_level === 1) || units[0];
            if (!selectedUnitId && firstVisible) {
                handleSelectUnit(firstVisible.id);
            }
        }
    }, [dispatch, selectedUnitId, singleSelect, units, unitsStatus]);

    // Handle loading and error states - show skeleton for both 'idle' and 'loading' to avoid flash of content
    if (unitsStatus === 'idle' || unitsStatus === 'loading') {
        // Render appropriate skeleton based on filter type
        if (singleSelect) {
            // Single unit filter skeleton - matches the red button style
            return (
                <div className="single-unit-filter-skeleton">
                    <div className="single-unit-filter-skeleton-text"></div>
                    <span className="single-unit-filter-skeleton-chevron">
                        <IconChevronDown size={20} stroke={2} />
                    </span>
                </div>
            );
        }
        
        // Multi-select unit filter skeleton
        return (
            <div className="unit-filter-container">
                <div className="unit-filter-control unit-filter-skeleton">
                    <div className="unit-filter-content">
                        <div className="unit-filter-skeleton-pill">
                            <div className="unit-filter-skeleton-text"></div>
                        </div>
                    </div>
                    <div className="unit-filter-divider" />
                    <div className="unit-filter-indicator">
                        <IconChevronDown size={24} stroke={2} />
                    </div>
                </div>
            </div>
        );
    }

    if (unitsStatus === 'failed') {
        return <div className="text-danger">Error: {unitsError}</div>;
    }

    if (singleSelect) {
        return (
            <SingleUnitFilter
                units={units}
                selectedUnitId={selectedUnitId}
                onSelectUnit={handleSelectUnit}
            />
        );
    }

    return (
        <UnitFilter
            units={units}
            selectedUnitIds={selectedUnitIds}
            onSelectUnits={handleSelectUnits}
            visibilityMode="frontend"
            flatMode={flatMode}
            hideDisabledOptions={hideDisabledOptions}
            disabledTooltip={disabledTooltip}
            leaderUnitIds={leaderUnitIds}
        />
    );
};

export default UnitFilterContainer;